import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth'
import { Variables } from 'relay-runtime'
import { strings, translate } from '../components/internationalization'
import { ShapeNode } from '../types'

// Returns a formatted date, Today, or Yesterday label depending on start and end dates provided
export const getHeading = (startTimestamp?: number, endTimestamp?: number) => {
  const startDate = startTimestamp ? new Date(startTimestamp * 1000) : undefined
  const endDate = endTimestamp ? new Date(endTimestamp * 1000) : undefined
  const options: Intl.DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }

  var returnDate
  if (!startDate && endDate) returnDate = endDate
  if (!startDate && !endDate) return returnDate = undefined
  if (startDate && endDate) returnDate = endDate
  if (startDate && !endDate) throw new Error('End date is required')

  // toDateString truncates time out of Date.
  const start = startDate?.toDateString()
  const end = endDate?.toDateString()
  const today = (new Date()).toDateString()
  const yesterday = (new Date(new Date().setDate(new Date().getDate() - 1))).toDateString()

  // Both start and end are defined, figure out the heading to return
  if (end === start) return undefined
  if (end == today) return translate(strings.CHAT_TODAY)
  if (end == yesterday) return translate(strings.CHAT_YESTERDAY)
  return returnDate?.toLocaleDateString(undefined, options)
}

export const toEpochSeconds = (dateStr: string): number =>
  Math.floor(new Date(dateStr).getTime() / 1000)

export const getHeadingFromISO = (startISO: string, endISO: string): string | undefined => {
  const startEpoch = toEpochSeconds(startISO)
  const endEpoch = toEpochSeconds(endISO)

  return getHeading(startEpoch, endEpoch)
}

export const getHeading2 = (startTimestamp?: string, endTimestamp?: string) => {
  const startDate = startTimestamp ? new Date(startTimestamp) : undefined
  const endDate = endTimestamp ? new Date(endTimestamp) : undefined
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  }

  let returnDate: Date | undefined

  if (!startDate && endDate) returnDate = endDate
  if (!startDate && !endDate) return undefined
  if (startDate && endDate) returnDate = endDate
  if (startDate && !endDate) throw new Error('End date is required')

  const start = startDate?.toDateString()
  const end = endDate?.toDateString()
  const today = new Date().toDateString()
  const yesterday = new Date(Date.now() - 86400000).toDateString() // 86400000ms = 1 day

  if (start === end) return undefined
  if (end === today) return translate(strings.CHAT_TODAY)
  if (end === yesterday) return translate(strings.CHAT_YESTERDAY)

  return returnDate?.toLocaleDateString(undefined, options)
}



export default function isDisplayed(scroller: HTMLElement | null, el: Element | null | undefined): boolean {
  if (!scroller || !el) return false

  const childRect = el.getBoundingClientRect()
  const childTop = childRect.top
  const childBottom = childRect.bottom

  const parentRect = scroller.getBoundingClientRect()
  const parentTop = parentRect.top
  const parentBottom = parentRect.bottom

  // relative position to parent instead of window viewPort
  const relativePosition = {
    top: childTop - parentTop,
    bottom: childBottom - parentBottom
  }

  const topWithinParent = relativePosition.top < scroller.clientHeight
  const bottomWithinParent = relativePosition.bottom > -Math.abs(scroller.clientHeight)

  const isPartiallyVisible = topWithinParent && bottomWithinParent
  return isPartiallyVisible
}

export const getTime = (date: Date | null) => {
  if (date) {
    const modifiedDate = new Date(date)
    const hoursAndMinutes = modifiedDate.getHours() + ':' + (modifiedDate.getMinutes() < 10 ? '0' : '') + modifiedDate.getMinutes()
    return hoursAndMinutes
  }
  return null
}

export const extractGuid = (s: string): string | null => {
  // Define a regular expression to match a GUID
  const guidRegex = /([0-9a-fA-F-]{36})/

  // Execute the regular expression on the provided string
  const match = s.match(guidRegex)

  // If a match is found, return the GUID part (second element in the array)
  if (match && match[1]) {
    return match[1]
  } else {
    // If no match is found, return null
    return null
  }
}

export const extractGuid3 = (encodedStr?: string): string | null => {
  try {
    if (!encodedStr) return null

    // Decode base64 (works in browser and Node.js >= 16)
    const decoded = Buffer.from(encodedStr, 'base64').toString('utf-8')

    // Regex pattern for GUID/UUID
    const guidRegex = /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/
    const match = decoded.match(guidRegex)

    return match ? match[0] : null
  } catch (error) {
    console.error('Error decoding or extracting GUID:', error)
    return null
  }
}

export const isEmptyObject = (obj: any) => typeof obj !== 'object' || obj == null || Object.keys(obj).length === 0

export const isMobile = () => window.matchMedia && window.matchMedia('(max-width: 992px)').matches


export const getAuthenticatedUser = async () => {
  const authUser = await getCurrentUser()

  const {
    tokens: session
  } = await fetchAuthSession()

  // Note that session will no longer contain refreshToken and clockDrift
  return {
    authUser,
    session,
    // authenticationFlowType: signInDetails.authFlowType
  }
}

export async function getToken(email: string): Promise<string | null> {
  const existingToken = getCookie('docu.idToken');
  if (existingToken) {
    return existingToken;
  }

  const {
    VITE_DOCUENGINE_PROTOCOL,
    VITE_DOCUENGINE_FQDN,
    VITE_DOCUENGINE_PORT,
    VITE_DOCUENGINE_IDP_PATH,
  } = import.meta.env;

  const url = `${VITE_DOCUENGINE_PROTOCOL}://${VITE_DOCUENGINE_FQDN}:${VITE_DOCUENGINE_PORT}${VITE_DOCUENGINE_IDP_PATH}`;

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      console.error("Token request failed:", res.status, errBody);
      return null;
    }

    const { token } = await res.json();
    if (typeof token === "string") {
      setCookie('docu.idToken', token, 30 / 1440); // Set cookie for 30min
      return token;
    }

    console.warn("No token received in response");
    return null;
  } catch (err) {
    console.error("Error during token fetch:", err);
    return null;
  }
}


export function getCookie(name: string): string | null | undefined {
  const cookies = document.cookie.split('; ');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.split('=');
    if (cookieName === name) {
      return decodeURIComponent(cookieValue);
    }
  }
  return null;
}

export function setCookie(name: string, value: string, exdays: number) {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  const expires = `expires=${d.toUTCString()}`;
  // Add a space after expires and before path to avoid concatenation issues
  document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/`;
}


// Sets the cookie's expiration to a time in the past, effectively removing it
export function deleteCookie(name: string) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
}

// For all queries, saves query variables to local storage. 
// For root query only, keeps track of main tab variables.
export function setVariables(name: string, variables: Variables | null | undefined) {
  // No undefined variables to avoid parameter mismatch errors on the graphQL server.
  // Also trim ' from boolean values otherwise graphQL server throws a type mismatch error. 
  const definedVariables = { ...variables }
  Object.keys(definedVariables).forEach(key => {
    // Do not drop relay internal variables.
    if (key.startsWith('__relay_internal__pv__')) return

    // Leave the value as is if there is one.
    if (Reflect.get(definedVariables, key) != null) return

    // Drop the variable because its value is null or undefined 
    //@ts-ignore
    delete definedVariables[key]
  })

  const strVariables = JSON.stringify(definedVariables)
  localStorage.setItem(`${name}-variables`, strVariables)

  // TODO find a way to avoid 2 copies of '*-variables' in the local storage
  if ((definedVariables.p_latitude || definedVariables.p_longitude) && !definedVariables.p_vacancy_guid) localStorage.setItem(`${name}-variables-main`, strVariables)

  return definedVariables
}

function base64UrlDecode(base64Url: string): string {
  // Replace base64 URL encoding characters with standard base64 characters
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  // Add necessary padding to the base64 string if required
  while (base64.length % 4) {
    base64 += '='
  }
  return Buffer.from(base64, 'base64').toString('utf-8')
}

function deepParseJSON(input: unknown): unknown {
  // Keep parsing if input is a string that looks like JSON
  while (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input)
      input = parsed
    } catch {
      break
    }
  }

  if (Array.isArray(input)) return input.map(deepParseJSON)

  if (typeof input === 'object' && input !== null) {
    const result: any = {}
    for (const [key, value] of Object.entries(input)) {
      result[key] = deepParseJSON(value)
    }
    return result
  }

  return input
}


function findDeepValueByKey(obj: unknown, keyToFind: string): unknown {
  if (typeof obj !== 'object' || obj === null) return undefined

  if (Object.prototype.hasOwnProperty.call(obj, keyToFind) && typeof obj === 'object') {
    return (obj as Record<string, unknown>)[keyToFind]
  }

  for (const key in obj as Record<string, unknown>) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = (obj as Record<string, unknown>)[key]

      if (typeof value === 'object') {
        const result = findDeepValueByKey(value, keyToFind)
        if (result !== undefined) {
          return result
        }
      }
    }
  }

  return undefined
}

function getClaimFromJwt(token: string, claimPath: string): any {
  const parts = token.split('.')
  if (parts.length !== 3) throw new Error('Invalid JWT token structure')

  const payloadStr = base64UrlDecode(parts[1])
  const payload = deepParseJSON(payloadStr)

  return findDeepValueByKey(payload, claimPath)
}

export function getClaim(claim: string) {
  const token = getCookie('idToken')
  if (!token) throw new Error('Invalid JWT token found')

  return getClaimFromJwt(token, claim)
}

export function getAuthenticated() {
  return localStorage.getItem('authenticated')?.toLowerCase() === 'true'
}


export const guidToClassName = (guid: string | number | undefined) => typeof guid === 'string' ? `${guid.replace(/-/g, '')}` : `${guid}`

export const extractGuid2 = (s: string): string | undefined => {
  // Define a regular expression to match a GUID
  const guidRegex = /([0-9a-fA-F-]{36})/

  // Execute the regular expression on the provided string
  const match = s.match(guidRegex)

  // If a match is found, return the GUID part (second element in the array)
  if (match && match[1]) {
    return match[1]
  } else {
    // If no match is found, return null
    return undefined
  }
}

export const getCognitoRegion = (userPoolId: string): string => {
  if (!userPoolId || typeof userPoolId !== 'string') {
    throw new Error('Invalid Cognito User Pool ID')
  }

  const parts = userPoolId.split('_')
  if (parts.length !== 2) {
    throw new Error('Unexpected Cognito User Pool ID format')
  }

  const [region] = parts
  const regionPattern = /^[a-z]{2}-[a-z]+-\d+$/
  if (!regionPattern.test(region)) {
    throw new Error('Invalid AWS region format')
  }

  return region
}

// Quick hack to work out a topic from a content
// TODO feed content into LLM to get the topic
export const getTopic = (content: string): string => {
  const sentences = content.split(/(?<=[.?!])\s+/)
  const first = sentences[0] || ''

  if (first.length >= 20 && first.length <= 30) {
    return first
  }

  const second = sentences[1] || ''
  const combined = (first + ' ' + second).trim()
  return combined.length > 30 ? combined.slice(0, 30) : combined
}

export const calculateSHA256 = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer()
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer)
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

// Utility method to retrieve a shape by name from a specified slide
export async function getShapeByName(context: any, shapeName: string, currentSlideId: string | null) {
  // Load the slides collection
  const slides = context.presentation.slides
  slides.load('items')
  await context.sync()

  console.log('slides.items.length', slides.items.length)
  if (slides.items.length <= 0) return null

  // Find the slide with the matching id
  const targetSlide = slides.items.find((slide: any) =>
    slide.id === currentSlideId
  )


  if (targetSlide) {
    const shapes = targetSlide.shapes
    shapes.load('items')
    await context.sync()

    for (const shape of shapes.items) {
      console.log(shape.name)
    }

    // Return the shape that matches the given name
    return shapes.items.find((item: any) => item.name === shapeName)
  }
  else console.log('targetSlide not found')

  return null
}

export interface SerializedShape {
  id?: string
  name: string
  left: number
  top: number
  width: number
  height: number
  type: PowerPoint.ShapeType
  zOrderPosition: number
  text: string
}

export type OutputSlide = {
  slide: {
    slide_key: string
    presentation_guid: string
  }
  shape: Array<SerializedShape>
}

async function serializeShape(context: PowerPoint.RequestContext, shape: PowerPoint.Shape): Promise<SerializedShape | undefined> {
  // Load everything up front
  shape.load(['id', 'name', 'left', 'top', 'width', 'height', 'zOrderPosition', 'type'])
  await context.sync()

  shape.tags.load('items')
  await context.sync()

  const commonProps = {
    id: shape.id,
    name: shape.name,
    left: Math.trunc(shape.left),
    top: Math.trunc(shape.top),
    width: Math.trunc(shape.width),
    height: Math.trunc(shape.height),
    zOrderPosition: Math.trunc(shape.zOrderPosition),
    type: shape.type as PowerPoint.ShapeType,
    text: '' // Default to empty, will be set if applicable
  }

  const result: SerializedShape = { ...commonProps }

  if (shape.textFrame && shape.textFrame.textRange) {
    shape.textFrame.textRange.load('text')
    await context.sync()
    result.text = shape.textFrame.textRange.text || ''
  }

  return result
}

async function serializeShapeRecursive(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape
): Promise<SerializedShape[]> {
  if (shape.type === PowerPoint.ShapeType.group) {
    const groupShapes: PowerPoint.ShapeScopedCollection = shape.group.shapes
    groupShapes.load('items')
    await context.sync()

    const allChildren: SerializedShape[] = []

    for (const child of groupShapes.items) {
      const childShapes = await serializeShapeRecursive(context, child)
      allChildren.push(...childShapes)
    }

    const groupData = await serializeShape(context, shape)

    // Return only non-null groupData along with children
    return groupData ? [groupData, ...allChildren] : [...allChildren]
  } else {
    const shapeData = await serializeShape(context, shape)
    return shapeData ? [shapeData] : []
  }
}

export async function getSlide(slide_id: string | null): Promise<OutputSlide | undefined> {
  try {
    if (!slide_id) {
      console.warn(`Slide with ID ${slide_id} not found.`)
      return undefined
    }

    return await PowerPoint.run(async (context: PowerPoint.RequestContext) => {
      const slides = context.presentation.slides
      slides.load('items')
      await context.sync()

      const slide = slides.items.find((s) => s.id.startsWith(slide_id))
      if (!slide) {
        console.warn(`Slide with ID ${slide_id} not found.`)
        return undefined
      }

      context.load(slide, ['id', 'index'])
      await context.sync()

      const slideShapes = slide.shapes
      slideShapes.load('items')
      await context.sync()

      if (slideShapes.items.length === 0) {
        console.warn(`No slideShapes found on slide ${slide_id}.`)
        return undefined
      }

      const results: SerializedShape[] = []

      for (const shape of slideShapes.items) {
        try {
          const shapesArray = await serializeShapeRecursive(context, shape)
          results.push(...shapesArray)
        } catch (error) {
          console.warn(`Failed to serialize shape:`, error)
          continue
        }
      }

      if (results.length === 0) {
        console.warn(`No bound shapes found on slide ${slide_id}.`)
        return undefined
      }

      const presentation_guid = await getOrCreatePresentationId()
      // Filter shapes with non-empty text
      const filteredShapes = results.filter((item) => item?.text?.trim())
      const outputSlide: OutputSlide = {
        slide: {
          slide_key: slide.id,
          presentation_guid
        },
        shape: filteredShapes.map(shape => ({
          shape_key: createShapeKey(slide.id, shape.id),
          name: shape.name,
          left: shape.left,
          top: shape.top,
          width: shape.width,
          height: shape.height,
          zOrderPosition: shape.zOrderPosition,
          text: shape.text,
          type: shape.type
        }))
      }

      return outputSlide
    })
  } catch (error) {
    console.error('Error in getSlide:', error)
    throw error
  }
}

export const createShapeKey = (slideId: string, shapeId?: string): string => `${slideId}::${shapeId}`

export const parseShapeKey = (key: string): { slideId: string; shapeId: string } | null => {
  const parts = key.split('::')
  if (parts.length !== 2) return null
  return {
    slideId: parts[0],
    shapeId: parts[1]
  }
}

export const getOrCreatePresentationId = async (): Promise<string> => {
  const settings = Office.context.document.settings
  const existingId = settings.get('uniquePresentationId')

  if (existingId) {
    console.log('✅ Existing Presentation ID found:', existingId)
    return existingId
  }

  const newId = crypto.randomUUID()
  settings.set('uniquePresentationId', newId)

  return new Promise((resolve, reject) => {
    settings.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        console.log('🆕 New Presentation ID saved:', newId)
        resolve(newId)
      } else {
        console.error('❌ Failed to save Presentation ID:', result.error.message)
        reject(result.error)
      }
    })
  })
}

export async function getShapesFromSlide(context: PowerPoint.RequestContext, slide: PowerPoint.Slide): Promise<PowerPoint.Shape[]> {
  const allShapes: PowerPoint.Shape[] = []

  async function collectShapes(shape: PowerPoint.Shape): Promise<void> {
    shape.load(['type', 'id'])
    await context.sync()

    if (shape.type === PowerPoint.ShapeType.group) {
      const groupShapes = shape.group.shapes
      groupShapes.load('items')
      await context.sync()

      for (const child of groupShapes.items) {
        await collectShapes(child) // Recursively handle nested groups
      }
    } else {
      allShapes.push(shape)
    }
  }

  const slideShapes = slide.shapes
  slideShapes.load('items')
  await context.sync()

  for (const shape of slideShapes.items) {
    await collectShapes(shape)
  }

  return allShapes
}

export async function getShapeById(context: PowerPoint.RequestContext, slide: PowerPoint.Slide, targetId: string): Promise<PowerPoint.Shape | undefined> {
  const allShapes = await getShapesFromSlide(context, slide)

  for (const shape of allShapes) {
    shape.load('id')
  }
  await context.sync()

  return allShapes.find((shape) => shape.id === targetId)
}

export async function highlightShapeByShapeKey(item: { node: ShapeNode }, color: string): Promise<void> {
  const [currentSlideId, shapeId] = item.node.shape.shape_key.split('::')

  if (!shapeId) {
    console.warn('No shapeId found in shapeKey:', item.node.shape.shape_key)
    return
  }

  try {
    await PowerPoint.run(async (context) => {
      if (!currentSlideId) {
        console.warn('No currentSlideId found in shapeKey:', item.node.shape.shape_key)
        return
      }

      const slide = context.presentation.slides.getItem(currentSlideId)
      const shape = await getShapeById(context, slide, shapeId)

      if (shape) {
        console.log('Shape found. Applying highlight...')
        shape.fill.setSolidColor(color)
      } else {
        console.warn(`Shape with ID ${shapeId} not found.`)
      }

      await context.sync()
    })
  } catch (error) {
    console.error('Error highlighting shape by shapeKey:', error)
  }
}

export const setShapeText = async (item: { node: ShapeNode }, text: string): Promise<void> => {
  const parsed = parseShapeKey(item.node.shape.shape_key)
  if (!parsed) return console.warn('Invalid shapeKey:', item.node.shape.shape_key)

  try {
    await PowerPoint.run(async (context) => {
      const shape = await getShapeById(
        context,
        context.presentation.slides.getItem(parsed.slideId),
        parsed.shapeId
      )

      if (!shape) return console.warn(`Shape with ID ${parsed.shapeId} not found.`)

      shape.textFrame.textRange.text = text
      await context.sync()
    })
  } catch (err) {
    console.error('Error setting shape text by shapeKey:', err)
  }
}
